import { Inject, Injectable, Optional } from '@angular/core';
import {
  firstValueFrom,
  timeout,
  filter,
  catchError,
  of,
  take,
  Observable,
} from 'rxjs';
import { ApiEndPoints, ApiMethod } from '../../core/services/const';
import { HttpService } from '../../core/services/http/http.service';
import { TourFeature } from '../config/tour-feature.enum';
import { TourStateStatus } from '../config/tour-state.enum';
import { CompanySettingsService } from 'src/app/modules/settings/services/company-settings.service';
import {
  CompanyService,
  CompanyDetails,
} from 'src/app/modules/settings/services/company.service';
import { CompanySettingsValidatorService } from 'src/app/modules/settings/services/company-settings-validator.service';
import { DepartmentService } from 'src/app/modules/organization/services/department.service';
import { SkillService } from 'src/app/modules/organization/services/skill.service';
import { withoutTaskProjects } from 'src/app/modules/organization/utils/task-project-filter';
import { EmployeeService } from 'src/app/modules/employee/services/employee.service';
import { Audience } from '../config/audience.enum';
import { AudienceResolver } from './audience-resolver.service';
import { TourStateStore } from './tour-state-store.service';
import { SubscriptionService } from 'src/app/modules/feature-toggle/services/subscription.service';
import { Feature } from 'src/app/modules/feature-toggle/enums/features.enum';
import { hasAnyTaskViewRight } from 'src/app/modules/tasks/nav/tasks-permission.helper';
import { UserPermissionsService } from 'src/app/modules/employee/services/user-permissions.service';
import { TaskSettingsService } from 'src/app/modules/tasks/settings/task-settings.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';

/**
 * Startpunkte (Tage relativ zu heute) der Schicht-Abfragen im Plan-Check von
 * „Erste Planung". `GET Calender/GetEvents/{withAbsence}/{start}/{dep}` liefert
 * ohne Mitarbeiter-Filter alle Schichten, die das Fenster `[start, start+30 T)`
 * beruehren — **inklusive Entwuerfe** (der Draft-Ausschluss im Handler gilt nur
 * im Einzel-Mitarbeiter-Zweig) — plus offene Schichten der Woche von `start`.
 * Ein `CurrentWeekEnd` laesst die Route nicht zu; ein laengerer Zeitraum geht
 * ohne neuen Endpoint nur ueber mehrere Fenster.
 *
 * Vier Fenster decken lueckenlos −37 … +83 Tage ab (~4 Monate), in der
 * Reihenfolge der Wahrscheinlichkeit: laufender Monat, dann die naechsten
 * beiden Monate (Erstplanung liegt typisch VORNE — Entwurf fuer „in 3–8
 * Wochen"), zuletzt der Monat davor (fruehere Planung, seither Pause).
 * Abbruch beim ersten Treffer; eine frische Firma ohne Schichten zahlt vier
 * leere Antworten.
 */
const SHIFT_WINDOW_START_OFFSETS_DAYS: readonly number[] = [-7, 23, 53, -37];

/** Vor dem Service Worker vorbei, wie `HttpService` (ngsw.json-Pruefung kostet sonst 2–3 s). */
const QUIET_HEADERS = new HttpHeaders({ 'ngsw-bypass': 'true' });

/**
 * Liest aus einer `GET /Tasks/scope`-Antwort, ob die Firma ueberhaupt schon
 * eine Aufgabe hat. Reine Funktion, damit die Formvarianten testbar bleiben:
 * die Antwort kommt je nach Endpoint als rohes DTO oder in einer
 * `ApiResponse`-Huelle (`Data`/`data`), und die Zaehler selbst koennen
 * Pascal- oder camelCase tragen.
 *
 * Bewusst `Total` und NICHT `Open`: eine bereits abgehakte Aufgabe zaehlt
 * genauso als „Ziel erreicht" wie eine offene — die Tour soll niemanden mehr
 * durch das Anlegen fuehren, der das Konzept schon benutzt.
 *
 * Unlesbare/fehlende Antwort ⇒ `false` (Tour bleibt eligible). Das folgt der
 * Detector-Philosophie „lieber einmal zu viel anbieten als faelschlich
 * unterdruecken".
 */
export function tasksScopeHasAnyTask(res: unknown): boolean {
  if (!res || typeof res !== 'object') return false;
  const root = res as Record<string, unknown>;
  const payload = (root['Data'] ?? root['data'] ?? root) as Record<
    string,
    unknown
  >;
  if (!payload || typeof payload !== 'object') return false;
  const counts = (payload['Counts'] ?? payload['counts']) as
    | Record<string, unknown>
    | undefined;
  if (!counts || typeof counts !== 'object') return false;
  const total = counts['Total'] ?? counts['total'];
  return typeof total === 'number' && Number.isFinite(total) && total > 0;
}

/**
 * Smart-Skip-Ergebnis eines Detektor-Checks:
 * - `completed`: Ziel komplett erreicht → Tour als Completed markieren.
 * - `partial`: Ziel teilweise erreicht → Tour als Started mit `lastStepId`
 *   markieren, damit der User sie via Checklist/Welcome-Card fortsetzen kann.
 * - `none`: Nichts erkannt → Tour bleibt eligible und wird normal angeboten.
 */
type CompletionCheckResult =
  | { kind: 'completed' }
  | { kind: 'partial'; lastStepId: string; totalStepCount: number }
  | { kind: 'none' };

/**
 * Smart-Skip: prüft pro Onboarding-Tour, ob das Ziel des Users bereits auf
 * anderem Weg erreicht wurde (z. B. Kunde hat Settings schon per Admin-Panel
 * ausgefuellt, Mitarbeiter manuell angelegt). In dem Fall wird die Tour
 * **lokal** als `completed` markiert — der EligibilityService bietet sie
 * damit nicht mehr als Nudge an (suppressAfterCompletion=true).
 *
 * Feingranulare Variante (Feature B): wenn das Ziel nur teilweise erreicht
 * ist (z. B. CompanyName gesetzt, aber WorkStructure fehlt), wird die Tour
 * als `started` mit sinnvollem `lastStepId` markiert. Der User kann sie
 * dann via Welcome-Card oder Checklist-FAB fortsetzen, ohne den bereits
 * ausgefuellten Teil nochmal durchlaufen zu muessen.
 *
 * Completed: bewusst lokal-only, kein Backend-Call — das waere ein
 * "Completion" ohne echten Durchlauf (keine Step-Zeit-Metrik, keine
 * Analytics). Beim naechsten Login laeuft der Check erneut auf dem
 * frisch geladenen Store.
 * Partial (Started): ruft `startTour` im Backend, damit der Fortschritt
 * persistiert ist und der Resume-Pfad in `TourCoordinator.startTour`
 * greift.
 */
@Injectable({ providedIn: 'root' })
export class CompletionDetector {
  /**
   * IDs der Tour-Steps, die fuer feingranulare `partial`-Markierung
   * relevant sind. Synchron gehalten mit den Tour-Configs; Aenderungen
   * dort müssen hier nachgezogen werden.
   *
   * CompanyInfo-Tour V3 (`settings-tutorial.config.ts`, 3 Steps):
   *   1. `company-info-basics`   — Block "Unternehmensinformationen"
   *      (Firmenname + Unternehmenstyp + Organisationsstruktur).
   *   2. `company-info-regional` — Block "Regionale Einstellungen"
   *      (Land + Region/Bundesland + Zeitzone + Sprache).
   *   3. `company-info-save`     — Kalender/Urlaub + Speichern.
   *
   *   Wenn alle Step-1-Felder gesetzt sind, aber im regionalen Block
   *   noch Pflichtfelder fehlen, hat der User Step 1 abgeschlossen →
   *   lastStepId zeigt auf `company-info-basics`, damit `startTour` ihn
   *   auf `company-info-regional` fortsetzt (Resume-Index = lastStepId + 1).
   */
  private static readonly COMPANY_INFO_TOTAL_STEPS = 3;
  private static readonly COMPANY_INFO_LAST_STEP_BEFORE_REGIONAL =
    'company-info-basics';
  /**
   * Field-Names, die der `CompanySettingsValidatorService` fuer Step 1
   * (Block "Unternehmensinformationen") zurueckgibt. Wenn keiner dieser
   * Field-Names in `missingFields` auftaucht, ist Step 1 abgeschlossen.
   */
  private static readonly STEP1_FIELD_NAMES: ReadonlySet<string> = new Set([
    'companyName',
    'companyTypeId',
    'workStructure',
  ]);

  constructor(
    private readonly companySettings: CompanySettingsService,
    private readonly companyService: CompanyService,
    private readonly companyValidator: CompanySettingsValidatorService,
    private readonly departmentService: DepartmentService,
    private readonly skillService: SkillService,
    private readonly employeeService: EmployeeService,
    private readonly state: TourStateStore,
    private readonly audienceResolver: AudienceResolver,
    private readonly http: HttpService,
    /**
     * Nur fuer den Aufgaben-Check (B-10). Beide Abhaengigkeiten sind bewusst
     * OPTIONAL mit `null`-Default: der bestehende Spec konstruiert den
     * Detector positionell mit neun Argumenten (`new CompletionDetector(...)`,
     * kein TestBed), und das soll er weiter duerfen. Fehlt eine der beiden,
     * meldet {@link checkTasks} `none` und die Tour bleibt schlicht eligible.
     *
     * ⚠️ `@Inject(...)` ist hier PFLICHT, nicht Deko: aus dem Union-Typ
     * `SubscriptionService | null` kann der Angular-Compiler keinen
     * Injection-Token ableiten ("No suitable injection token for parameter").
     * Der explizite Token loest das, `@Optional()` haelt die Nullbarkeit
     * ehrlich.
     */
    @Optional()
    @Inject(SubscriptionService)
    private readonly subscription: SubscriptionService | null = null,
    @Optional()
    @Inject(UserPermissionsService)
    private readonly userPermissions: UserPermissionsService | null = null,
    /**
     * Firmen-Schalter `TaskSettings.ModuleEnabled` (Server antwortet sonst 403
     * `TASK_MODULE_DISABLED`). Optional aus demselben Grund wie oben.
     */
    @Optional()
    @Inject(TaskSettingsService)
    private readonly taskSettings: TaskSettingsService | null = null,
    /**
     * Roher `HttpClient` NUR fuer die Aufgaben-Probe: `HttpService.requestCall`
     * zeigt strukturierte Fehler (z. B. 500 mit `message`) in seiner eigenen
     * Pipe als Toast — VOR jedem `catchError` des Aufrufers. Ein Hintergrund-
     * Check darf das nie. Die Interceptoren (Auth, Client-Info) greifen trotzdem.
     */
    @Optional()
    @Inject(HttpClient)
    private readonly rawHttp: HttpClient | null = null
  ) {}

  /**
   * Laeuft alle Detektor-Checks einmal durch und markiert die entsprechenden
   * Touren als completed (ganz fertig) bzw. started (teilweise erreicht).
   */
  async detectAndMarkCompleted(): Promise<void> {
    const checks: Array<
      [TourFeature, () => Promise<CompletionCheckResult>, boolean]
    > = [
      // Drittes Tupel-Element `dataGoalResolvable`: ob die Tour rein ein
      // DATEN-Ziel verfolgt und deshalb als erledigt gilt, sobald die Daten
      // existieren — auch aus einem `Started`-Stand MIT Fortschritt. Nur fuer
      // die BINAEREN Daten-Tours (Abteilung/Skills/Mitarbeiter): ihr einziger
      // Zweck ist das Anlegen des Datensatzes; ist er da, ist die gefuehrte
      // Tour gegenstandslos und soll NICHT als „Tour fortsetzen?"-Nag haengen
      // bleiben (F7). CompanyInfo ist ausgenommen (false) — dort traegt
      // `Started` echte Resume-/Step-1-Semantik (teilweise ausgefuelltes
      // Formular), die der `partial`-Pfad bedient.
      [TourFeature.CompanyInfo, () => this.checkCompanyInfo(), false],
      [TourFeature.AddSkills, () => this.checkSkills(), true],
      [TourFeature.FirstDepartment, () => this.checkDepartments(), true],
      [TourFeature.FirstEmployee, () => this.checkEmployees(), true],
      // Aufgaben (B-10): binaeres Daten-Ziel — „erste Aufgabe angelegt".
      // `dataGoalResolvable: true` wie bei Abteilung/Skills/Mitarbeiter: wer
      // die erste Aufgabe unterwegs anlegt, soll danach keinen
      // „Tour fortsetzen?"-Nag mehr sehen.
      [TourFeature.Tasks, () => this.checkTasks(), true],
    ];

    for (const [tourId, check, dataGoalResolvable] of checks) {
      const current = this.state.getStateSync(tourId);

      // F7 (User-Entscheidung „Ziel erreicht ⇒ Tour erledigt"): Bei den
      // binaeren Daten-Tours darf ein `Started`-Stand bei erfuellter
      // Daten-Bedingung aufgeloest werden — UNABHAENGIG vom Fortschritt
      // (frueher nur completedStepCount === 0). Sonst erscheint nach dem
      // Anlegen des Skills/der Abteilung/des Mitarbeiters weiter ein
      // „Tour fortsetzen? — bei Schritt 3/7 pausiert"-Nag, obwohl das Ziel
      // laengst erreicht ist. CompanyInfo bleibt ausgenommen.
      const isResolvableStarted =
        dataGoalResolvable && current.status === TourStateStatus.Started;

      // Explizit dismissed/completed Touren NICHT anfassen. Ein `Started` wird
      // nur fuer die Daten-Tours (isResolvableStarted) aufgeloest; CompanyInfo-
      // `Started` bleibt unberuehrt (Step-1-Resume-Semantik). AutoCompleted
      // darf der Detector revidieren, wenn die Daten-Bedingung nicht mehr passt.
      if (
        current.status === TourStateStatus.Completed ||
        (current.status === TourStateStatus.Started && !isResolvableStarted) ||
        current.status === TourStateStatus.Dismissed
      ) {
        continue;
      }

      try {
        const result = await check();
        if (result.kind === 'completed') {
          if (isResolvableStarted) {
            // Daten-Ziel erreicht aus einem `Started`-Stand (ggf. mit
            // Fortschritt) → hart auf AutoCompleted ziehen. Raeumt auch
            // `lastStepId`, sodass kein Resume-Pfad die Tour wieder aufgreift.
            await this.state.forceDataGoalAutoComplete(tourId);
          } else if (current.status !== TourStateStatus.AutoCompleted) {
            await this.state.markAutoCompleted(tourId);
          }
          // sonst schon AutoCompleted und weiterhin gueltig — nichts tun.
        } else if (result.kind === 'partial' && !isResolvableStarted) {
          await this.state.markAutoStarted(
            tourId,
            result.lastStepId,
            result.totalStepCount
          );
        } else if (current.status === TourStateStatus.AutoCompleted) {
          // Daten-Bedingung erfuellt nicht mehr → AutoCompleted-Mark
          // zuruecknehmen. Tour wird wieder im Funnel angeboten.
          await this.state.revertAutoCompleted(tourId);
        }
        // 'none' bei nicht-AutoCompleted → nichts tun, Tour bleibt eligible.
      } catch {
        // Offline-/Fehler-tolerant: wenn der Check nicht klappt, wird die
        // Tour weiter angeboten. Lieber einmal zuviel angeboten als faelsch-
        // lich unterdrueckt.
      }
    }

    // Mentor "Erste Planung" (mentor-entry) wird separat behandelt: sein
    // Daten-Ziel ist „≥1 Wochenmuster, Schicht oder Aufgabe" (eigener Check, nicht
    // eine der vier Listen oben) und der Handoff nach der W1-Wegwahl laesst die
    // Entry-Tour bewusst offen. Auch hier gilt „Ziel erreicht ⇒ erledigt", aus
    // jedem Started-Stand. Deshalb nicht im generischen Loop, sondern hier.
    await this.detectFirstPlanning();
  }

  /**
   * "Erste Planung" (mentor-entry): erledigt, sobald ein ECHTER Plan
   * existiert — ≥1 Wochenmuster, ≥1 Schicht (auch Entwurf) oder ≥1 Aufgabe
   * (Planung ueber Aufgaben/Auftraege statt Schichten, Kunde 29). Damit ist der Status ehrlich:
   * blosses Waehlen eines Weges im W1-Schritt ("Gleich den Wochenplan" /
   * "Eine einzelne Schicht" / "Nur umschauen") schliesst den Schritt NICHT
   * mehr ab (der Handoff im TourCoordinator unterbindet das) — erst das
   * tatsaechliche Planen zaehlt.
   *
   * - Completed/Dismissed → unberuehrt (bewusste Nutzer-/Endzustaende).
   * - Plan existiert → AutoCompleted (auch aus Started-mit-Fortschritt).
   * - Plan existiert nicht (mehr) + aktuell AutoCompleted → zuruecknehmen.
   * Nur fuer Planer (Admin/TeamLeader) — andere haben weder die Tour noch
   * Zugriff auf Plan-/Muster-Daten.
   *
   * Public, damit der {@link TriggerMatcher} die Auswertung auch ausserhalb
   * des Login-Kontexts erneut anstossen kann (nach `template.created`/
   * `shift.created`/`shift.published`) — sonst wuerde der „Erste Planung"-
   * Schritt erst nach einem Reload als erledigt erscheinen. Idempotent +
   * offline-tolerant, daher gefahrlos mehrfach aufrufbar.
   */
  async detectFirstPlanning(): Promise<void> {
    const tourId = TourFeature.MentorEntry;
    const current = this.state.getStateSync(tourId);
    // Dismissed = User hat den Schritt bewusst weggeklickt → respektieren.
    // (Completed wird NICHT mehr uebersprungen: seit dem Handoff-Fix kann
    // mentor-entry nicht mehr per Tour abgeschlossen werden, ein vorhandenes
    // `completed` ist daher entweder durch einen echten Plan gedeckt — dann
    // bleibt es — oder eine Altlast ohne Plan, die hier bereinigt wird.)
    if (current.status === TourStateStatus.Dismissed) return;

    const audience = await firstValueFrom(
      this.audienceResolver.currentAudience$
    );
    const isPlanner =
      audience.includes(Audience.Admin) ||
      audience.includes(Audience.TeamLeader);
    if (!isPlanner) return;

    let planExists: boolean;
    try {
      planExists = await this.firstPlanExists();
    } catch {
      // Offline-/Fehler-tolerant: lieber nichts aendern als ein falscher Status.
      return;
    }

    if (planExists) {
      // Echter Plan → erledigt. Aus jedem offenen Zustand (auch Started-mit-
      // Fortschritt) aufloesen; ein bereits (auto-)completed Stand bleibt.
      if (
        current.status !== TourStateStatus.AutoCompleted &&
        current.status !== TourStateStatus.Completed
      ) {
        await this.state.forceDataGoalAutoComplete(tourId);
      }
    } else {
      // Kein echter Plan → "Erste Planung" ist NICHT erledigt. Done-Marker
      // bereinigen, damit der Goldpfad den Schritt wieder offen zeigt.
      if (current.status === TourStateStatus.AutoCompleted) {
        await this.state.revertAutoCompleted(tourId);
      } else if (
        current.status === TourStateStatus.Completed ||
        !!current.completedAt
      ) {
        // Altlast aus dem frueheren W1-Bug (Backend kann ein echtes Completed
        // nicht zuruecknehmen) → lokal oeffnen. Frische In-Progress-Staende
        // (Started OHNE completedAt) bleiben unberuehrt.
        this.state.forceReopenLocal(tourId);
      }
    }
  }

  /**
   * True, sobald die Firma einen ersten Plan hat: ≥1 Wochenmuster
   * (Mentor-Primaerweg) ODER ≥1 Schicht (Entwurf zaehlt) ODER ≥1 Aufgabe
   * (Kunden, die ueber Aufgaben/Auftraege statt ueber Schichten planen —
   * Kunde 29; Schritte eines Auftrags sind Aufgaben und zaehlen mit).
   *
   * Reihenfolge nach Kosten: Muster-Liste, Schichten im laufenden Monat, der
   * Aufgaben-Zaehler (nur bei verfuegbarem Modul), dann die weiteren
   * Schicht-Fenster ({@link SHIFT_WINDOW_START_OFFSETS_DAYS}). Abbruch beim
   * ersten Treffer. Was ausserhalb von −37 … +83 Tagen liegt, bleibt
   * unentdeckt — bewusst, im Sinne der Detector-Philosophie „lieber einmal zu
   * viel anbieten als faelschlich unterdruecken".
   */
  private async firstPlanExists(): Promise<boolean> {
    const templates = await this.firstHttpArray(
      this.http.requestCall(ApiEndPoints.GetTemplateList, ApiMethod.GET)
    );
    if (templates.length > 0) return true;

    const [firstWindow, ...laterWindows] = SHIFT_WINDOW_START_OFFSETS_DAYS;
    if (await this.shiftsExistInWindow(firstWindow)) return true;

    if (await this.anyTaskExists()) return true;

    for (const offset of laterWindows) {
      if (await this.shiftsExistInWindow(offset)) return true;
    }
    return false;
  }

  /**
   * Eine Schicht-Abfrage ueber `GetEvents/{withAbsence}/{start}/{department}`:
   * withAbsence=false → keine Abwesenheiten, department=null → alle
   * Abteilungen. Das Backend-Fenster ist `[start, start+30 T)` (Ueberlappung),
   * Entwuerfe inklusive.
   */
  private async shiftsExistInWindow(startOffsetDays: number): Promise<boolean> {
    const start = new Date();
    start.setDate(start.getDate() + startOffsetDays);
    const startStr = start.toISOString().slice(0, 10);
    const shifts = await this.firstHttpArray(
      this.http.requestCall(
        `${ApiEndPoints.GetEvents}/false/${startStr}/null`,
        ApiMethod.GET
      )
    );
    return shifts.length > 0;
  }

  /**
   * Hat die Firma (aus Sicht des Nutzers) mindestens eine Aufgabe?
   * `GET /Tasks/scope` → `Counts.Total > 0` — billigster vorhandener Leser:
   * ein einzelner Zaehler, filter-neutral, nicht archiviert, ohne
   * Projekt-Ausschluss (Auftrags-Schritte sind `TaskItem`s und zaehlen mit),
   * view-scoped wie die Uebersicht.
   *
   * Vorpruefungen, BEVOR ein Request rausgeht (sonst 402/403 bei jedem Login):
   *   1. Abo-Feature `TASK_MANAGEMENT` (Seed-Id 64) aktiv,
   *   2. mindestens ein Task-View-Recht (`hasAnyTaskViewRight`, DEC-09 —
   *      fehlender `Tasks`-Block ⇒ false),
   *   3. Firmen-Schalter `ModuleEnabled` an (sonst 403 `TASK_MODULE_DISABLED`).
   * Das ist dieselbe Bedingung wie `TasksGateService` fuer die Flaeche
   * `manager`, nur einmalig statt als Strom.
   *
   * Der Abruf laeuft ueber den rohen `HttpClient`: jeder Fehler (auch 5xx mit
   * Meldung) und jede unlesbare Antwort ergibt still `false` — kein Toast.
   * Fehlt eine der optionalen Abhaengigkeiten, ebenfalls `false`, ohne Aufruf.
   */
  private async anyTaskExists(): Promise<boolean> {
    if (
      !this.subscription ||
      !this.userPermissions ||
      !this.taskSettings ||
      !this.rawHttp
    ) {
      return false;
    }

    // Abo-Feature. `ensureFeaturesLoaded` ist idempotent und cached; ohne den
    // Await koennte `hasFeature` bei einem frischen Login noch auf dem leeren
    // Initial-Set stehen und den Check faelschlich ueberspringen.
    try {
      await firstValueFrom(
        this.subscription.ensureFeaturesLoaded().pipe(
          take(1),
          timeout(2500),
          catchError(() => of(false))
        )
      );
    } catch {
      return false;
    }
    if (!this.subscription.hasFeature(Feature.TASK_MANAGEMENT)) return false;

    if (!hasAnyTaskViewRight(this.userPermissions.CurrentUserPermissions)) {
      return false;
    }

    // `ensureLoaded` faellt bei Fehlern selbst auf die Defaults zurueck (Modul
    // an). Hier reicht das: der Aufruf unten ist ohnehin still.
    try {
      const settings = await firstValueFrom(
        this.taskSettings.ensureLoaded().pipe(
          take(1),
          timeout(2500),
          catchError(() => of(null))
        )
      );
      if (!settings?.moduleEnabled) return false;
    } catch {
      return false;
    }

    // Route als Literal wie in `modules/tasks/services/task-api.service.ts`
    // (`const TASKS = '/Tasks'`) — der Aufgaben-Bereich fuehrt seine Routen
    // bewusst nicht in `ApiEndPoints`.
    try {
      const res = await firstValueFrom(
        this.rawHttp
          .get(`${environment.apiURL}/Tasks/scope`, { headers: QUIET_HEADERS })
          .pipe(
            take(1),
            timeout(2500),
            catchError(() => of(null))
          )
      );
      return tasksScopeHasAnyTask(res);
    } catch {
      return false;
    }
  }

  /**
   * Liest die erste Emission eines One-Shot-HTTP-Calls und extrahiert ein
   * Array aus den gaengigen Response-Formen (rohes Array, `Data`, `data`,
   * `DataList`). Timeout/Fehler → leeres Array (sicherer Default).
   */
  private async firstHttpArray(obs$: Observable<any>): Promise<unknown[]> {
    try {
      const res = await firstValueFrom(
        obs$.pipe(
          take(1),
          timeout(2500),
          catchError(() => of(null))
        )
      );
      if (Array.isArray(res)) return res;
      if (Array.isArray(res?.Data)) return res.Data;
      if (Array.isArray(res?.data)) return res.data;
      if (Array.isArray(res?.DataList)) return res.DataList;
      return [];
    } catch {
      return [];
    }
  }

  // ===== Detektor-Checks =====

  /**
   * CompanyInfo-Tour: Feingranular.
   *
   * Single Source of Truth: `CompanySettingsValidatorService.validateEssentialFields`
   * — derselbe Validator, der den orangen "Pflichtfelder fehlen"-Banner in
   * `general-settings` erzeugt. Dadurch sind Detector und UI-Banner garantiert
   * synchron (Bug-Fix 2026-05-07: Detector hatte vorher nur 2 von 6
   * Pflichtfeldern geprueft und die Tour faelschlich als AutoCompleted
   * markiert, obwohl `Unternehmenstyp` und `Region/Bundesland` noch fehlten).
   *
   * Mapping auf Tour V3:
   * - Alle 6 Pflichtfelder gesetzt (`isValid`) → completed.
   * - Step-1-Felder (`companyName`, `companyTypeId`, `workStructure`) alle
   *   gesetzt, aber im regionalen Block fehlen noch Pflichtfelder
   *   (`country`, `region`, `timezone`) → partial, Resume bei Step 2.
   * - Sonst (Step-1-Luecken) → none, Tour bleibt eligible und wird frisch
   *   ab Step 1 angeboten.
   */
  private async checkCompanyInfo(): Promise<CompletionCheckResult> {
    const settings = this.companySettings.CompanySettings;
    if (!settings) return { kind: 'none' };

    const details = await this.firstCompanyDetails();
    const result = this.companyValidator.validateEssentialFields(
      settings,
      // Validator nutzt durchgaengig optional chaining → null ist sicher.
      details as CompanyDetails
    );

    if (result.isValid) return { kind: 'completed' };

    const missingFieldNames = new Set(
      result.missingFields.map((f) => f.fieldName)
    );
    const step1Complete = ![...CompletionDetector.STEP1_FIELD_NAMES].some(
      (f) => missingFieldNames.has(f)
    );
    if (step1Complete) {
      return {
        kind: 'partial',
        lastStepId: CompletionDetector.COMPANY_INFO_LAST_STEP_BEFORE_REGIONAL,
        totalStepCount: CompletionDetector.COMPANY_INFO_TOTAL_STEPS,
      };
    }
    return { kind: 'none' };
  }

  /**
   * Liest den aktuellen `CompanyDetails`-Snapshot aus dem BehaviorSubject.
   * Analog zu `firstArray` mit 2.5s-Timeout — wenn der HTTP-Fetch noch
   * laeuft oder fehlschlaegt, returnen wir `null` und der Validator
   * meldet entsprechend Pflichtfelder als fehlend (sicherer Default).
   */
  private async firstCompanyDetails(): Promise<CompanyDetails | null> {
    try {
      return await firstValueFrom(
        this.companyService.companyDetails$.pipe(
          take(1),
          timeout(2500),
          catchError(() => of(null as unknown as CompanyDetails))
        )
      );
    } catch {
      return null;
    }
  }

  /**
   * AddSkills-Tour: binaer. Der Skill-Erstell-Flow zeigt einen kompletten
   * Create-Dialog — ein sinnvoller Zwischenstand existiert hier nicht.
   */
  private async checkSkills(): Promise<CompletionCheckResult> {
    this.skillService.getAllSkillList();
    const list = await this.firstArray(this.skillService.AllSkillList);
    return list.length > 0 ? { kind: 'completed' } : { kind: 'none' };
  }

  /**
   * FirstDepartment-Tour: binaer. Tour fuehrt durch den Create-Flow; wer
   * eine Abteilung hat, hat das Ziel erreicht.
   *
   * K29 E1: Ein Auftrag („Aufträge mit Schritten“, `IsTaskProject`) ist technisch ein
   * Department, aber keine Abteilung — wer nur Aufträge angelegt hat, hat die Tour nicht
   * erledigt. Klassische Projekte zählen weiter.
   */
  private async checkDepartments(): Promise<CompletionCheckResult> {
    this.departmentService.getDepartementList();
    const list = withoutTaskProjects(
      (await this.firstArray(this.departmentService.departementList)) as object[]
    );
    return list.length > 0 ? { kind: 'completed' } : { kind: 'none' };
  }

  /**
   * FirstEmployee-Tour: zaehlt erst ab dem **zweiten** Eintrag als erledigt.
   *
   * Hintergrund: der Admin-/Manager-User selbst wird beim Company-Setup
   * automatisch als Mitarbeiter Nr. 1 angelegt (PN 1000). Wuerde Smart-Skip
   * schon bei `length > 0` greifen, waere die Tour fuer jeden frischen
   * Kunden bereits "BEREITS EINGERICHTET" und liesse sich nicht mehr im
   * Funnel durchspielen — der eigentliche Zweck (ersten Team-Mitarbeiter
   * anlegen) wird erst durch ein zweites Employee-Record erreicht.
   * Department/Skills haben kein Auto-Default und bleiben deshalb bei
   * `length > 0`.
   */
  private async checkEmployees(): Promise<CompletionCheckResult> {
    // Defense-in-Depth: Die FirstEmployee-Tour zielt auf Admin/TeamLeader
    // (Mitarbeiter anlegen). Reguläre Mitarbeiter haben keine Berechtigung
    // fuer `/Employee/EmployeeList` (403) und die Tour ist fuer sie ohnehin
    // nicht eligible. Skip mit `none` — Tour bleibt fuer andere User-Klassen
    // sichtbar, fuer Employees keine Backend-Probe.
    const audience = await firstValueFrom(
      this.audienceResolver.currentAudience$
    );
    const isOnboardingAudience =
      audience.includes(Audience.Admin) ||
      audience.includes(Audience.TeamLeader);
    if (!isOnboardingAudience) return { kind: 'none' };

    this.employeeService.getEmployeeList();
    const list = await this.firstArray(this.employeeService.employeeList);
    return list.length > 1 ? { kind: 'completed' } : { kind: 'none' };
  }

  /**
   * Tasks-Tour: binaer. Erledigt, sobald die Firma mindestens eine Aufgabe hat
   * (`GET /Tasks/scope` → `Counts.Total > 0`, siehe {@link anyTaskExists} —
   * dort auch die Modul-/Rechte-Vorpruefungen und der stille Abruf). Hier
   * zusaetzlich nur die Audience Admin/TeamLeader (wie {@link checkEmployees}).
   */
  private async checkTasks(): Promise<CompletionCheckResult> {
    const audience = await firstValueFrom(
      this.audienceResolver.currentAudience$
    );
    const isOnboardingAudience =
      audience.includes(Audience.Admin) ||
      audience.includes(Audience.TeamLeader);
    if (!isOnboardingAudience) return { kind: 'none' };

    return (await this.anyTaskExists())
      ? { kind: 'completed' }
      : { kind: 'none' };
  }

  /**
   * Wartet auf die erste **nicht-leere** Array-Emission eines Services.
   *
   * Die meisten Listen-Services sind `BehaviorSubject([])`-basiert: das initiale
   * `[]` kommt sofort, der echte Wert erst nach dem HTTP-Fetch. Wir filtern das
   * initiale Leere raus, damit wir nicht faelschlich "keine Daten" sehen.
   *
   * Bei echten Neu-Kunden (ohne Daten) greift der Timeout: wir returnen `[]`
   * und die Tour wird normal angeboten — das ist der gewuenschte Fall.
   */
  private async firstArray(obs$: any): Promise<unknown[]> {
    try {
      const value = await firstValueFrom(
        obs$.pipe(
          filter((v: unknown) => Array.isArray(v) && v.length > 0),
          take(1),
          timeout(2500),
          catchError(() => of([]))
        )
      );
      return Array.isArray(value) ? value : [];
    } catch {
      return [];
    }
  }
}
